import { PAGE_SIZE } from '@tshmanager/chat-shared'
import { h, Fragment } from 'preact'
import { useEffect, useMemo } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { createChatStore } from '../chat-store.js'
import { connectChatCable } from '../chat-cable.js'
import { MessageList } from './MessageList.jsx'
import { Composer } from './Composer.jsx'
import { TypingIndicator } from './TypingIndicator.jsx'
import { ContactProfilePanel } from './ContactProfilePanel.jsx'
import { chatApi } from '../chat-api.js'
import { batch } from '@preact/signals'
import { leaveRoom, purgeRoom } from '../leave-room.js'
import { t } from '../chat-i18n.js'
import { floatPanelRight, FLOAT_PANEL_WIDTH } from '../../../lib/widget_manager.js'
import { useWidgetLayout } from '../../../lib/use_widget_layout.js'

const PANEL_WIDTH = FLOAT_PANEL_WIDTH

/**
 * FloatPanel — a floating chat panel in the bottom-right corner of the screen.
 *
 * Each float panel has its own isolated store so that multiple panels can be
 * open simultaneously without sharing activeRoomId state. Its cable uses
 * `globals: false` (Room/Typing only) — dock owns PresenceChannel heartbeat.
 * The dock store (passed via `dockStore`) is used for close/minimize/persist.
 *
 * Panel positions cascade from the right via widget_manager (dock + call occupancy).
 * State (open panels, minimized status) is persisted to localStorage.
 *
 * Props: { dockStore, panelRoomId, personId, tenantId, panelIndex, minimized }
 *
 * dockStore.floatPanels signal is the source of truth for which panels are open.
 */
export function FloatPanel({ dockStore, panelRoomId, personId, personName = '', tenantId, panelIndex = 0, minimized = false }) {
  useWidgetLayout()
  // Each float panel gets its own store and cable connection, scoped to panelRoomId.
  const { store, cable } = useMemo(() => {
    const s = createChatStore({
      roomId:         panelRoomId,
      personId,
      personName,
      tenantId:       tenantId || '',
      canManageRooms: dockStore.canManageRooms,
    })
    // Room/Typing only — dock owns PresenceChannel + PersonChannel heartbeat.
    const c = connectChatCable({
      store:    s,
      personId,
      tenantId: tenantId || '',
      globals:  false,
    })
    return { store: s, cable: c }
  }, [panelRoomId]) // eslint-disable-line

  // Load messages on mount — regardless of minimized state so that when the user
  // expands the panel messages are already there.
  useEffect(() => {
    store.loadingRoomMsgs.value = panelRoomId
    chatApi.messages.list(panelRoomId).then(async res => {
      if (res.ok) {
        const data = await res.json
        const msgs = data.messages ?? data ?? []
        const hasMore = data.has_more ?? msgs.length >= PAGE_SIZE
        const oldest = msgs[0]?.id ?? null
        batch(() => {
          store.messagesByRoom.value = { [panelRoomId]: msgs }
          store.hasMoreByRoom.value  = { [panelRoomId]: hasMore }
          if (oldest) store.oldestIdByRoom.value = { [panelRoomId]: oldest }
        })
      }
    }).catch(() => { /* ignore */ })
      .finally(() => { store.loadingRoomMsgs.value = null })

    return () => { cable.disconnect() }
  }, [panelRoomId]) // eslint-disable-line

  // Subscribe to RoomChannel (sets active_room_id on server) and mark as read
  // only when the panel is visible. Minimized panels must not suppress unread
  // notifications on mobile — the user hasn't actually seen the messages.
  useEffect(() => {
    if (minimized) {
      cable.unsubscribeRoom(panelRoomId)
      return
    }

    cable.subscribeRoom(panelRoomId)
    chatApi.messages.read(panelRoomId).catch(() => { /* non-fatal */ })
  }, [minimized, panelRoomId]) // eslint-disable-line

  // Sync room name from dock store's room list (if available) to the panel store.
  useEffect(() => {
    const room = dockStore.rooms.value.find(r => r.id === panelRoomId)
    if (room) store.rooms.value = [room]
  }, [dockStore.rooms.value, panelRoomId]) // eslint-disable-line

  const rightOffset = floatPanelRight(panelIndex)

  function handleMinimize() {
    dockStore.floatPanels.value = dockStore.floatPanels.value.map(p =>
      p.roomId === panelRoomId ? { ...p, minimized: !p.minimized } : p
    )
  }

  function handleClose() {
    dockStore.floatPanels.value = dockStore.floatPanels.value.filter(p => p.roomId !== panelRoomId)
  }

  async function handleLeave(e) {
    e.stopPropagation()
    await leaveRoom({ store, cable, roomId: panelRoomId, dockStore })
  }

  async function handlePurge(e) {
    e.stopPropagation()
    await purgeRoom({ store, cable, roomId: panelRoomId, dockStore })
  }

  const room     = store.rooms.value.find(r => r.id === panelRoomId)
    ?? dockStore.rooms.value.find(r => r.id === panelRoomId)
  const roomName = room ? (room.display_name || room.name || 'Chat') : 'Chat'
  const canPurge = !!room?.can_purge

  // Render the panel into #chat-float-container (outside the dock window) so
  // that position:fixed works correctly even when the dock window has a CSS
  // transform applied (transforms create a new containing block for fixed
  // descendants, which would otherwise trap the panel inside the dock popup).
  const portalTarget =
    document.getElementById('chat-float-container') ?? document.body

  const panel = (
    <>
    <div
      class={`chat-float-shadow chat-float-enter${minimized ? ' chat-float-shadow--minimized' : ''}`}
      style={{
        right: `${rightOffset}px`,
        width: `${PANEL_WIDTH}px`,
      }}
    >
    <div
      class={`chat-float-panel${minimized ? ' chat-float-panel--minimized' : ''}`}
    >
      {/* Panel title bar */}
      <div class="chat-float-panel-header" onClick={handleMinimize} style={{ cursor: 'pointer' }}>
        <span class="chat-float-panel-title">{roomName}</span>
        <div class="chat-float-panel-actions">
          <button
            type="button"
            class="chat-float-panel-btn chat-float-panel-btn--danger"
            title={t('chat.rooms.leave')}
            onClick={handleLeave}
          >
            <i class="bi bi-box-arrow-right" />
          </button>
          {canPurge && (
            <button
              type="button"
              class="chat-float-panel-btn chat-float-panel-btn--danger"
              title={t('chat.rooms.purge')}
              onClick={handlePurge}
            >
              <i class="bi bi-trash" />
            </button>
          )}
          <button
            type="button"
            class="chat-float-panel-btn"
            title={minimized ? t('chat.dock.expand') : t('chat.dock.minimize')}
            onClick={e => { e.stopPropagation(); handleMinimize() }}
          >
            <i class={`bi bi-${minimized ? 'chevron-up' : 'chevron-down'}`} />
          </button>
          <button
            type="button"
            class="chat-float-panel-btn"
            title={t('chat.dock.close')}
            onClick={e => { e.stopPropagation(); handleClose() }}
          >
            <i class="bi bi-x-lg" />
          </button>
        </div>
      </div>

      {/* Panel body — always in DOM, CSS controls slide visibility */}
      <div class="chat-float-body">
        <MessageList
          store={store}
          cable={cable}
          scrollPersistKey={`tsh:chatFloatScroll:${personId}:${panelRoomId}`}
        />
        <TypingIndicator store={store} />
        <Composer store={store} cable={cable} roomId={panelRoomId} personId={personId} />
      </div>
    </div>
    </div>

    {/* Contact profile modal — uses this panel's isolated store */}
    <ContactProfilePanel store={store} />
    </>
  )

  return createPortal(panel, portalTarget)
}
